import React from 'react';

function segmentWidth(count, total) {
  if (!total || !count) return 0;
  return Math.max(0, Math.min(100, (count / total) * 100));
}

const GoLoginJobProgressBar = ({ progress, title }) => {
  if (!progress) return null;

  const total = progress.total || 0;
  const processed = progress.processed || 0;
  const isSync = progress.type === 'sync-cookies';
  const isDelete = progress.type === 'delete';
  const isGologinRemove = progress.type === 'gologin-remove';
  const isAttach = progress.type === 'attach';
  const phase = progress.phase || '';
  const succeeded = isSync
    ? (progress.synced || progress.succeeded || 0)
    : (progress.succeeded || 0);
  const importedNoCookie = progress.importedNoCookie || 0;
  const failed = progress.failed || 0;
  const skipped = progress.skipped || 0;
  const running = progress.status === 'running';
  const done = progress.status === 'completed' || progress.status === 'failed';

  const pending = Math.max(0, total - processed);

  const defaultTitle = isAttach
    ? (phase === 'gologin'
      ? 'Importing to GoLogin…'
      : phase === 'loading'
        ? 'Loading accounts…'
        : 'Adding accounts to VA…')
    : isGologinRemove
      ? 'Removing from GoLogin…'
      : isDelete
        ? 'Removing from GoLogin + Wingr…'
        : isSync
          ? 'Syncing AUTH cookies…'
          : 'Importing to GoLogin…';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {title || defaultTitle}
        </p>
        <p className="text-sm text-gray-500 whitespace-nowrap">
          {processed} / {total}
          {running && (
            <span className="ml-2 inline-block w-2 h-2 rounded-full bg-indigo-500 animate-pulse align-middle" />
          )}
        </p>
      </div>

      <div className="h-4 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
        <div
          className="bg-emerald-500 transition-all duration-300"
          style={{ width: `${segmentWidth(succeeded, total)}%` }}
          title={`${succeeded} success`}
        />
        {!isSync && !isDelete && !isGologinRemove && (progress.type === 'import' || phase === 'gologin') && (
          <div
            className="bg-orange-400 transition-all duration-300"
            style={{ width: `${segmentWidth(importedNoCookie, total)}%` }}
            title={`${importedNoCookie} imported without cookie`}
          />
        )}
        <div
          className="bg-red-500 transition-all duration-300"
          style={{ width: `${segmentWidth(failed, total)}%` }}
          title={`${failed} failed`}
        />
        <div
          className="bg-amber-400 transition-all duration-300"
          style={{ width: `${segmentWidth(skipped, total)}%` }}
          title={`${skipped} skipped`}
        />
        {running && pending > 0 && (
          <div
            className="bg-gray-300 dark:bg-gray-600 transition-all duration-300"
            style={{ width: `${segmentWidth(pending, total)}%` }}
            title={`${pending} remaining`}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
          ✓ {succeeded} {isDelete || isGologinRemove ? 'removed' : isAttach && phase !== 'gologin' ? 'attached' : 'success'}
        </span>
        {!isSync && !isDelete && !isGologinRemove && (progress.type === 'import' || phase === 'gologin') && importedNoCookie > 0 && (
          <span className="text-orange-600 dark:text-orange-400 font-medium">⚠ {importedNoCookie} no cookie</span>
        )}
        <span className="text-red-600 dark:text-red-400 font-medium">✗ {failed} failed</span>
        {skipped > 0 && (
          <span className="text-amber-600 dark:text-amber-400 font-medium">⊘ {skipped} skipped</span>
        )}
        {isAttach && phase === 'gologin' && progress.folderName && (
          <span className="text-gray-500">Folder: {progress.folderName}</span>
        )}
      </div>

      {progress.currentUsername && running && (
        <p className="text-xs text-gray-500 truncate">Current: {progress.currentUsername}</p>
      )}

      {done && progress.status === 'failed' && (
        <p className="text-sm text-red-600">{progress.error || 'Job failed'}</p>
      )}

      {done && progress.status === 'completed' && progress.result?.failedSamples?.length > 0 && (
        <div className="text-xs text-red-600 space-y-1">
          {progress.result.failedSamples.map((sample) => (
            <p key={`${sample.username}-${sample.error}`} className="truncate">
              {sample.username}: {sample.error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default GoLoginJobProgressBar;
